import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Trash2, Clock, ArrowLeft, ShieldAlert, AlertTriangle } from 'lucide-react';
import type { EmailMessage } from '@shared/types';
interface EmailDetailModalProps {
  email: EmailMessage | null;
  onClose: () => void;
  onDelete: (id: string) => void;
}
export function EmailDetailModal({ email, onClose, onDelete }: EmailDetailModalProps) {
  if (!email) return null;
  const isHighRisk = (email.phishingScore ?? 0) > 50;
  // Prepare iframe content for HTML emails
  const iframeSrcDoc = email.bodyHtml ? `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; line-height: 1.6; color: #333; background: #fff; margin: 0; padding: 20px; }
          img { max-width: 100%; height: auto; }
          a { color: #8b5cf6; }
        </style>
      </head>
      <body>${email.bodyHtml}</body>
    </html>
  ` : null;
  return (
    <Dialog open={!!email} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0 glass-card border-white/10 overflow-hidden">
        {isHighRisk && (
          <div className="bg-destructive/95 text-destructive-foreground px-6 py-3 flex items-start gap-3 text-sm font-medium animate-pulse">
            <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" />
            <div>
              <p>Phát hiện rủi ro bảo mật</p>
              <p className="text-xs font-normal text-destructive-foreground/80 mt-1">
                Hệ thống đánh dấu email này có khả năng lừa đảo cao. Tránh nhấp vào liên kết lạ.
              </p>
            </div>
          </div>
        )}
        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/5">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="overflow-hidden">
              <DialogTitle className="text-xl font-bold truncate pr-4">{email.subject}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Chỉ số an toàn: <span className={isHighRisk ? 'text-destructive' : 'text-green-500'}>
                  {100 - (email.phishingScore ?? 0)}/100
                </span>
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => {
                onDelete(email.id);
                onClose();
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Xóa</span>
            </Button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                {email.fromName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{email.fromName}</p>
                  {email.isSpam && <AlertTriangle className="h-3 w-3 text-yellow-500" />}
                </div>
                <p className="text-sm text-muted-foreground">{email.fromAddress}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-white/5 px-3 py-1.5 rounded-full">
              <Clock className="h-3.5 w-3.5" />
              {format(email.receivedAt, 'PPpp', { locale: vi })}
            </div>
          </div>
          <Separator className="bg-white/10" />
        </div>
        <div className="flex-1 overflow-hidden flex flex-col">
          {iframeSrcDoc ? (
            <iframe
              title="Email Content"
              srcDoc={iframeSrcDoc}
              className="w-full h-full border-none bg-white"
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
            />
          ) : (
            <ScrollArea className="flex-1 px-6 pb-6">
              <p className="whitespace-pre-wrap text-foreground/90 leading-relaxed text-sm py-4">
                {email.bodyText || email.preview}
              </p>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}